/**
 * Collapsible sections whose open/closed state is remembered and applied to every case.
 *
 * ## The problem
 *
 * The admin case page is long — status, references, workflow, the advocate's review, the
 * submission summary, documents, two message panes, progress and the full history. Any given
 * admin uses a handful of those routinely and scrolls past the rest, on every case, all day.
 *
 * ## Remembered per section, not per case
 *
 * Collapsing "Full history" means "I don't read the history", not "I don't read *this* case's
 * history" — so the state is keyed by section id alone and every case opens the same way. That is
 * the whole point: the layout is a working preference, and having to re-collapse the same four
 * panels on the next case would make the feature cost more than it saves.
 *
 * ## localStorage, per browser
 *
 * The same mechanism as the sidebar's collapsed rail (`admin_sidebar_collapsed`), and for the same
 * reason: this is one person's view preference, not a property of the business. Site config would
 * be wrong — it is shared, so one admin tidying their page would rearrange everyone else's. It
 * outlives the session deliberately, unlike a search box (see lib/stickySearch.js): a stale layout
 * hides nothing that a click does not bring straight back, whereas a stale *filter* hides rows and
 * reads as missing data.
 *
 * Every read and write is wrapped — localStorage throws outright in some privacy modes, and a
 * panel that cannot remember whether it was open is a far smaller problem than one that crashes
 * the page it lives on.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const STORE = "dharmatn.admin.sections";

const readAll = () => {
  try {
    const raw = window.localStorage.getItem(STORE);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
};

/**
 * Re-read before writing rather than merging into a value captured at mount.
 *
 * Ten sections are on screen at once, each holding its own copy of this map. Writing a remembered
 * copy back would resurrect whatever the others had changed since — collapse two panels and the
 * second click would silently re-open the first.
 */
const writeOne = (id, collapsed) => {
  try {
    window.localStorage.setItem(STORE, JSON.stringify({ ...readAll(), [id]: collapsed }));
  } catch { /* storage unavailable — the layout just won't survive a reload */ }
};

/**
 * Force a section open ahead of navigating to it — the message-count icons in the admin queue
 * link straight to `#case-messages` on the case page, and a link that lands on a *collapsed*
 * panel (because this admin keeps it closed on every other case) would show nothing at all. Call
 * this before the navigation, not after: `useSectionCollapsed` only reads localStorage once, on
 * its own mount, so setting it while the target page doesn't exist yet is what makes it open
 * already-expanded rather than flashing collapsed-then-open.
 */
export function expandSection(id) {
  writeOne(id, false);
}

/**
 * `[collapsed, toggle]` for one section. Sections default to **open**: a page that hid things on
 * first visit would be a page whose content an admin has to go looking for.
 */
export function useSectionCollapsed(id) {
  const [collapsed, setCollapsed] = useState(() => Boolean(readAll()[id]));
  // The stored map is the authority when the id changes, so a section rendered in a loop (the two
  // message panes) picks up its own state rather than keeping its neighbour's.
  useEffect(() => { setCollapsed(Boolean(readAll()[id])); }, [id]);
  const toggle = useCallback(() => {
    setCollapsed(prev => { writeOne(id, !prev); return !prev; });
  }, [id]);
  return [collapsed, toggle];
}

/** The chevron. Rotates rather than swapping glyphs, so the control reads as one thing moving. */
export const SectionChevron = ({ collapsed }) => (
  <ChevronDown
    className={`w-4 h-4 shrink-0 text-[#6B7280] transition-transform ${collapsed ? "-rotate-90" : ""}`}
    strokeWidth={1.5}
  />
);

/**
 * A header row that toggles its section.
 *
 * `<button>` rather than a click handler on a div, so it is reachable by keyboard and announced as
 * a control — these headers are now the only way to get some of the page back.
 *
 * `actions` renders *outside* the button. Nesting a button inside a button is invalid HTML and
 * React will warn; more practically, "View / Edit document" sitting inside the toggle would
 * collapse the panel on its way to opening a dialog.
 */
export function SectionHeader({ title, collapsed, onToggle, actions, testid, className = "" }) {
  return (
    <div className={`flex flex-row items-center justify-between gap-2 space-y-0 ${className}`}>
      <button
        type="button" onClick={onToggle} data-testid={testid}
        aria-expanded={!collapsed}
        title={collapsed ? "Expand" : "Collapse"}
        className="flex items-center gap-2 min-w-0 text-left group"
      >
        <SectionChevron collapsed={collapsed}/>
        <span className="font-serif-display text-lg flex items-center gap-2 group-hover:text-white transition-colors truncate">
          {title}
        </span>
      </button>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

/**
 * A shadcn `Card` whose body collapses. The common case — most of this page is one of these.
 *
 * The header stays visible when collapsed, and it has to: it is the label telling the reader what
 * is behind it and the control that brings it back.
 */
export function CollapsibleCard({ id, title, actions, children, testid, headerTestid, className }) {
  const [collapsed, toggle] = useSectionCollapsed(id);
  return (
    <Card id={id} data-testid={testid} className={className}>
      <CardHeader className={collapsed ? "pb-6" : undefined}>
        <SectionHeader
          title={title} collapsed={collapsed} onToggle={toggle} actions={actions}
          testid={headerTestid || `section-${id}`}
        />
      </CardHeader>
      {!collapsed && children}
    </Card>
  );
}

/**
 * The dark panel variant, for the two sections that are not shadcn Cards — the status strip and
 * the references block. Same styling they already had, so collapsing them does not also restyle
 * them.
 */
export function CollapsiblePanel({ id, title, children, testid, className = "" }) {
  const [collapsed, toggle] = useSectionCollapsed(id);
  return (
    <div className={`bg-[#111623] border border-white/10 rounded-2xl ${className}`} data-testid={testid}>
      <div className="px-5 py-3">
        <button
          type="button" onClick={toggle} data-testid={`section-${id}`} aria-expanded={!collapsed}
          title={collapsed ? "Expand" : "Collapse"}
          className="flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[#6B7280] hover:text-white transition-colors"
        >
          <SectionChevron collapsed={collapsed}/>{title}
        </button>
      </div>
      {!collapsed && children}
    </div>
  );
}

export default CollapsibleCard;
